import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Theme } from "@rjsf/bootstrap-4";
import { utils, withTheme } from "@rjsf/core";

import ErrorScreen from "../../../../components/error/ErrorScreen";
import Loader from "../../../../components/Loader";
import { alertContext } from "../../../../components/modal/ModalComponent";
import useFetch from "../../../../components/useFetch";
import { getCokkie } from "../../../login/Login";
import { CustomWidget } from "../../../../components/widgets/CustomWidgets";
import DataConfiguration2 from "../../../../components/modal/NavPanes/DatConfiguration2";
import {
  ArrowRightIcon,
  BackIcon,
  CheckCircleIcon,
  CloseIcon,
  ModifyOperationsIcon,
} from "../../../Common/SvgIcons/WorkflowIcons";
import {
  buildTaskLikeWidgets,
  enhanceTaskLikeUiSchema,
  normalizeFieldName,
  replaceUnderscoresInSchemaTitles,
  sanitizeUiSchema,
} from "../DatasetFormUI";
import "../AddDataset.scss";
import "../generate_task_collection/GenerateTaskCollection.scss";
import "../../train/Experiments.scss";
import { CheckIcon } from "lucide-react";
import {
  applyExperimentLayoutUi,
  applySplitPresetToFormData,
  applySplitTypeToFormData,
  CUSTOM_SPLIT_PRESET,
  ExperimentFieldTemplate,
  ExperimentObjectFieldTemplate,
  ExperimentSplitField,
  ExperimentSplitTypeField,
  getCustomSplitTypeValueFromSchema,
  getSplitRatiosFromFormData,
  getSplitValidationMessage,
  hydrateSplitFormData,
  isSplitObjectSchema,
} from "../../train/AddExperiment/ExperimentFormLayout";

const registry = utils.getDefaultRegistry();
const defaultFileWidget = registry.widgets["FileWidget"];

Theme.widgets = {
  ...Theme.widgets,
  FileWidget: defaultFileWidget,
};

const Form = withTheme(Theme);

const isChoiceSchema = (schemaNode = {}) =>
  Array.isArray(schemaNode.enum) ||
  Array.isArray(schemaNode.oneOf) ||
  Array.isArray(schemaNode.anyOf);

const matchesNormalizedField = (target, fieldName = "", schemaNode = {}) =>
  [fieldName, schemaNode?.title].some(
    (fieldValue) => normalizeFieldName(fieldValue) === target
  );

const isModifyOperationFieldName = (fieldName = "") => {
  const normalizedFieldName = normalizeFieldName(fieldName);

  return (
    normalizedFieldName.includes("operation") ||
    normalizedFieldName.includes("modify") ||
    normalizedFieldName.includes("transformation") ||
    normalizedFieldName === "action"
  );
};

const isModifyOperationChoiceField = (fieldName = "", schemaNode = {}) => {
  const normalizedFieldName = normalizeFieldName(fieldName || schemaNode?.title);

  return (
    schemaNode?.type === "string" &&
    isChoiceSchema(schemaNode) &&
    isModifyOperationFieldName(normalizedFieldName)
  );
};

const isModifyOptionChoiceField = (fieldName = "", schemaNode = {}) => {
  return (
    schemaNode?.type === "string" &&
    isChoiceSchema(schemaNode) &&
    matchesNormalizedField("applyaugmentationsas", fieldName, schemaNode)
  );
};

const isApplyAugmentationsChoiceField = (fieldName = "", schemaNode = {}) => {
  return (
    schemaNode?.type === "string" &&
    isChoiceSchema(schemaNode) &&
    matchesNormalizedField("applyaugmentations", fieldName, schemaNode)
  );
};

const enhanceModifyOperationsUiSchema = (
  schemaNode,
  uiNode = {},
  fieldName = ""
) => {
  if (!schemaNode || typeof schemaNode !== "object") {
    return uiNode || {};
  }

  const nextUiNode = { ...(uiNode || {}) };

  if (isModifyOperationChoiceField(fieldName, schemaNode)) {
    nextUiNode["ui:widget"] = "modifyOperationCards";
  }

  if (isApplyAugmentationsChoiceField(fieldName, schemaNode)) {
    nextUiNode["ui:widget"] = "modifyAugmentationChoice";
  }

  if (isModifyOptionChoiceField(fieldName, schemaNode)) {
    nextUiNode["ui:widget"] = "modifyOptionCards";
  }

  if (schemaNode.type === "boolean") {
    nextUiNode["ui:widget"] = "modifyOperationSwitch";
    nextUiNode["ui:label"] = false;
    nextUiNode["ui:options"] = {
      ...(nextUiNode["ui:options"] || {}),
      label: false,
    };
  }

  if (schemaNode.properties && typeof schemaNode.properties === "object") {
    Object.keys(schemaNode.properties).forEach((key) => {
      nextUiNode[key] = enhanceModifyOperationsUiSchema(
        schemaNode.properties[key],
        nextUiNode[key],
        key
      );
    });
  }

  if (schemaNode.items && typeof schemaNode.items === "object") {
    nextUiNode.items = enhanceModifyOperationsUiSchema(
      schemaNode.items,
      nextUiNode.items,
      fieldName
    );
  }

  if (schemaNode.dependencies && typeof schemaNode.dependencies === "object") {
    Object.keys(schemaNode.dependencies).forEach((depKey) => {
      const dependency = schemaNode.dependencies[depKey];

      if (Array.isArray(dependency?.oneOf)) {
        nextUiNode[depKey] = {
          ...(nextUiNode[depKey] || {}),
          oneOf: dependency.oneOf.map((branch, index) =>
            enhanceModifyOperationsUiSchema(
              branch,
              nextUiNode?.[depKey]?.oneOf?.[index],
              depKey
            )
          ),
        };
      }
    });
  }

  return nextUiNode;
};

const COMPOUND_LABEL_TOKENS =
  "gaussian|horizontal|vertical|random|additive|multiplicative|optical|distortion|noise|flip|blur|crop|rotate|scale|shift|shear|contrast|brightness|saturation|affine|elastic|perspective|dropout|sharpen|emboss|clahe|cutout|pad|resize|translate|channel|shuffle|fog|snow|rain|shadow|sunflare|motion|coarse|fine|grid";

const getReadableOperationLabel = (label = "") => {
  const raw = String(label || "").trim();
  if (!raw) return "";

  let spaced = raw
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .replace(/(\d)([a-zA-Z])/g, "$1 $2");

  if (!/\s/.test(spaced)) {
    spaced = spaced
      .replace(
        new RegExp(
          `(${COMPOUND_LABEL_TOKENS})(?=${COMPOUND_LABEL_TOKENS}|$)`,
          "gi"
        ),
        "$1 "
      )
      .trim();
  }

  return spaced
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => {
      if (!word) return word;
      if (word === word.toUpperCase() && word.length > 1) return word;
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
    })
    .join(" ");
};

const getReadableSentenceLabel = (label = "") => {
  const readableLabel = getReadableOperationLabel(label);

  if (!readableLabel) return "";

  return `${readableLabel.charAt(0).toUpperCase()}${readableLabel
    .slice(1)
    .toLowerCase()}`;
};

const getModifyOperationFieldClassName = (
  name = "",
  propertyCount = 0,
  propertySchema = {}
) => {
  const normalizedName = normalizeFieldName(name);
  const normalizedTitle = normalizeFieldName(propertySchema?.title);

  if (
    normalizedName === "applyaugmentationsas" ||
    normalizedTitle === "applyaugmentationsas"
  ) {
    return "task-field-full modify-augmentations-as-field";
  }

  if (
    normalizedName.includes("probability") ||
    normalizedTitle.includes("probability")
  ) {
    return "task-field-full modify-probability-field";
  }

  if (
    propertyCount === 1 ||
    propertySchema?.type === "object" ||
    propertySchema?.type === "array" ||
    isModifyOperationFieldName(name) ||
    normalizedName.includes("description") ||
    normalizedName.includes("guideline") ||
    normalizedName.includes("label") ||
    normalizedName.includes("details") ||
    normalizedName.includes("content") ||
    normalizedName.includes("summary")
  ) {
    return "task-field-full";
  }

  return "task-field-half";
};

const ModifyOperationsFieldTemplate = (fieldProps) => {
  const {
    id,
    classNames,
    label,
    name,
    required,
    description,
    errors,
    help,
    children,
    hidden,
    schema: fieldSchema,
    uiSchema = {},
  } = fieldProps;

  if (hidden) {
    return <>{children}</>;
  }

  const options = uiSchema?.["ui:options"] || {};
  const resolvedLabel = getReadableOperationLabel(
    fieldSchema?.title || label || name || ""
  );
  const schemaDescription = String(fieldSchema?.description || "").trim();
  const normalizedLabel = normalizeFieldName(
    fieldSchema?.title || label || name || resolvedLabel
  );
  const isRedundantDescription =
    !schemaDescription ||
    normalizeFieldName(schemaDescription) === normalizedLabel ||
    normalizeFieldName(schemaDescription) ===
      normalizeFieldName(resolvedLabel);
  const isStructuredField =
    fieldSchema?.type === "object" || fieldSchema?.type === "array";
  const shouldHideMeta =
    fieldSchema?.type === "object" ||
    uiSchema?.["ui:label"] === false ||
    options.label === false ||
    uiSchema?.["ui:widget"] === "hidden" ||
    fieldSchema?.type === "boolean";

  return (
    <div
      className={`${classNames || ""} task-collection-field ${isStructuredField ? "task-collection-field--group" : ""
        } ${!resolvedLabel ? "task-collection-field--no-label" : ""}`}
    >
      {resolvedLabel &&
        !shouldHideMeta &&
        resolvedLabel !== "Apply Augmentations As" && (
        <label htmlFor={id} className="form-label task-collection-field__label">
          {resolvedLabel}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      {description && !shouldHideMeta && !isRedundantDescription && (
        <div className="task-collection-field__description form-help">{description}</div>
      )}
      <div className="task-collection-field__control">{children}</div>
      {errors}
      {!shouldHideMeta && help}
    </div>
  );
};

const ModifyOperationChoiceWidget = ({
  value,
  options,
  disabled,
  readonly,
  onChange,
}) => {
  const enumOptions = options?.enumOptions || [];
  const canEdit = !disabled && !readonly;

  if (enumOptions.length === 0) {
    return null;
  }

  return (
    <div className="modify-operation-choice" role="radiogroup">
      {enumOptions.map((option) => {
        const isSelected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            className={`modify-operation-choice__card ${isSelected ? "is-selected" : ""
              }`}
            disabled={!canEdit}
            aria-pressed={isSelected}
            onClick={() => canEdit && onChange(option.value)}
          >
            <span className="modify-operation-choice__icon">
              <ModifyOperationsIcon className="wf-icon" />
            </span>
            <span className="bold-text">
              {getReadableOperationLabel(option.label)}
            </span>
            <span className="modify-operation-choice__check">
              <CheckIcon className="wf-icon text-white" />
            </span>
          </button>
        );
      })}
    </div>
  );
};

const ModifyOptionCardsWidget = ({
  value,
  options,
  disabled,
  readonly,
  onChange,
  schema,
}) => {
  const enumOptions = options?.enumOptions || [];
  const canEdit = !disabled && !readonly;
  const selectedValue = value ?? schema?.default ?? enumOptions?.[0]?.value;

  useEffect(() => {
    if (value !== undefined && value !== null && value !== "") return;
    if (selectedValue === undefined || selectedValue === null || !canEdit) return;

    onChange(selectedValue);
  }, [canEdit, onChange, selectedValue, value]);

  return (
    <div className="modify-option-choice-block">
      <div className="modify-option-choice" role="radiogroup">
        {enumOptions.map((option) => {
          const isSelected = option.value === selectedValue;

          return (
            <button
              key={option.value}
              type="button"
              className={`modify-option-choice__card ${isSelected ? "is-selected" : ""
                }`}
              disabled={!canEdit}
              aria-pressed={isSelected}
              onClick={() => canEdit && onChange(option.value)}
            >
              <span className="modify-option-choice__radio" aria-hidden="true" />
              <span className="modify-option-choice__label">
                {getReadableSentenceLabel(option.label)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ModifyAugmentationChoiceWidget = ({
  value,
  options,
  disabled,
  readonly,
  onChange,
  schema,
}) => {
  const enumOptions = options?.enumOptions || [];
  const canEdit = !disabled && !readonly;
  const selectedValue = value ?? schema?.default ?? enumOptions?.[0]?.value;

  useEffect(() => {
    if (value !== undefined && value !== null && value !== "") return;
    if (selectedValue === undefined || selectedValue === null || !canEdit) return;

    onChange(selectedValue);
  }, [canEdit, onChange, selectedValue, value]);

  return (
    <div className="modify-augmentation-choice" role="radiogroup">
      {enumOptions.map((option) => {
        const isSelected = option.value === selectedValue;

        return (
          <button
            key={option.value}
            type="button"
            className={`modify-augmentation-choice__option ${isSelected ? "is-selected" : ""
              }`}
            disabled={!canEdit}
            aria-pressed={isSelected}
            onClick={() => canEdit && onChange(option.value)}
          >
            <span className="modify-augmentation-choice__label">
              {getReadableOperationLabel(option.label)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const ModifyOperationSwitchWidget = ({
  value,
  disabled,
  readonly,
  onChange,
  schema,
}) => {
  const checked = Boolean(value ?? schema?.default);
  const canEdit = !disabled && !readonly;

  return (
    <label
      className={`modify-operation-switch ${checked ? "is-checked" : ""} ${!canEdit ? "is-disabled" : ""
        }`}
      aria-label={schema?.title || "Toggle operation"}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={!canEdit}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="modify-operation-switch__slider" />
    </label>
  );
};

const getDefaultChoiceValue = (schemaNode = {}) => {
  if (schemaNode.default !== undefined) {
    return schemaNode.default;
  }

  if (Array.isArray(schemaNode.enum) && schemaNode.enum.length > 0) {
    return schemaNode.enum[0];
  }

  const choice = schemaNode.oneOf?.[0] || schemaNode.anyOf?.[0];

  return choice?.const ?? choice?.enum?.[0];
};

const hydrateFormDataDefaults = (schemaNode, formData = {}) => {
  if (!schemaNode) return formData;

  let hydratedFormData = formData;

  // RJSF resolves one dependency level per pass. Iterate until stable so
  // defaults inside a selected version's nested dependency are also applied.
  for (let pass = 0; pass < 10; pass += 1) {
    const nextFormData = utils.getDefaultFormState(
      schemaNode,
      hydratedFormData,
      schemaNode
    );

    if (JSON.stringify(nextFormData) === JSON.stringify(hydratedFormData)) {
      return nextFormData;
    }

    hydratedFormData = nextFormData;
  }

  return hydratedFormData;
};

const applyDependencyDefaultsOnSelectionChange = (
  schemaNode,
  nextFormData = {},
  previousFormData = {}
) => {
  const dependencies = schemaNode?.dependencies || {};
  let resetFormData = nextFormData;
  let shouldHydrate = false;

  Object.entries(dependencies).forEach(([dependencyKey, dependencySchema]) => {
    const branches = dependencySchema?.oneOf || dependencySchema?.anyOf;
    const nextValue = nextFormData?.[dependencyKey];
    const previousValue = previousFormData?.[dependencyKey];

    if (!Array.isArray(branches) || Object.is(nextValue, previousValue)) return;

    const matchingBranch = branches.find((branch) => {
      const discriminator = branch?.properties?.[dependencyKey];

      return (
        discriminator?.const === nextValue ||
        discriminator?.enum?.includes(nextValue)
      );
    });

    if (!matchingBranch) return;

    if (resetFormData === nextFormData) {
      resetFormData = { ...nextFormData };
    }

    // Branch-owned values belong to the selected source version. Remove the
    // previous branch's values before asking RJSF to populate the new branch.
    branches.forEach((branch) => {
      Object.keys(branch?.properties || {}).forEach((propertyKey) => {
        if (propertyKey !== dependencyKey) {
          delete resetFormData[propertyKey];
        }
      });
    });

    shouldHydrate = true;
  });

  return shouldHydrate
    ? hydrateFormDataDefaults(schemaNode, resetFormData)
    : nextFormData;
};

const shouldShowAugmentationFields = (value) => {
  const normalizedValue = normalizeFieldName(value);

  if (!normalizedValue) {
    return false;
  }

  return !["no", "none", "false", "disabled"].includes(normalizedValue);
};

const getAnnotationFilterKind = (value = "") => {
  const normalizedValue = normalizeFieldName(value);

  if (
    normalizedValue.includes("unannotated") ||
    normalizedValue.includes("missingannotations")
  ) {
    return "unannotated";
  }

  if (
    normalizedValue.includes("annotated") ||
    normalizedValue.includes("onlyannotations")
  ) {
    return "annotated";
  }

  return null;
};

const isLabelsFilterName = (value = "") => {
  const normalizedValue = normalizeFieldName(value);

  return (
    normalizedValue === "label" ||
    normalizedValue === "labels" ||
    normalizedValue.includes("filterbylabel") ||
    normalizedValue.includes("filterlabel") ||
    normalizedValue.includes("labelfilter")
  );
};

const getValueAtPath = (source = {}, path = []) =>
  path.reduce((currentValue, pathKey) => currentValue?.[pathKey], source);

const setValueAtPath = (source = {}, path = [], value) => {
  if (path.length === 0) {
    return source;
  }

  const [pathKey, ...remainingPath] = path;
  const nextSource = Array.isArray(source) ? [...source] : { ...(source || {}) };

  if (remainingPath.length === 0) {
    nextSource[pathKey] = value;
    return nextSource;
  }

  nextSource[pathKey] = setValueAtPath(
    nextSource[pathKey],
    remainingPath,
    value
  );

  return nextSource;
};

const setUiSchemaAtPath = (source = {}, path = [], updater) => {
  if (path.length === 0) {
    return source || {};
  }

  const [pathKey, ...remainingPath] = path;
  const nextSource = { ...(source || {}) };

  if (remainingPath.length === 0) {
    nextSource[pathKey] = updater(nextSource[pathKey] || {});
    return nextSource;
  }

  nextSource[pathKey] = setUiSchemaAtPath(
    nextSource[pathKey],
    remainingPath,
    updater
  );

  return nextSource;
};

const getBooleanTogglePath = (schemaNode = {}, path = []) => {
  if (schemaNode?.type === "boolean") {
    return path;
  }

  const booleanProperties = Object.entries(schemaNode?.properties || {}).filter(
    ([, propertySchema]) => propertySchema?.type === "boolean"
  );

  if (booleanProperties.length === 0) {
    return null;
  }

  const [toggleKey] =
    booleanProperties.find(
      ([key, propertySchema]) =>
        normalizeFieldName(key) === "apply" ||
        normalizeFieldName(propertySchema?.title) === "apply"
    ) || booleanProperties[0];

  return [...path, toggleKey];
};

const collectAnnotationFilterPaths = (
  schemaNode = {},
  path = [],
  result = {}
) => {
  Object.entries(schemaNode?.properties || {}).forEach(([key, propertySchema]) => {
    const currentPath = [...path, key];
    const filterKind = getAnnotationFilterKind(
      `${key} ${propertySchema?.title || ""}`
    );

    if (filterKind) {
      const togglePath = getBooleanTogglePath(propertySchema, currentPath);

      if (togglePath) {
        result[filterKind] = togglePath;
      }
    }

    collectAnnotationFilterPaths(propertySchema, currentPath, result);
  });

  return result;
};

const getLabelsFilterPath = (schemaNode = {}, path = []) => {
  const propertyEntries = Object.entries(schemaNode?.properties || {});

  for (const [key, propertySchema] of propertyEntries) {
    const currentPath = [...path, key];
    const isLabelsFilter = isLabelsFilterName(
      `${key} ${propertySchema?.title || ""}`
    );

    if (isLabelsFilter) {
      const togglePath = getBooleanTogglePath(propertySchema, currentPath);

      if (togglePath) {
        return togglePath;
      }
    }

    const nestedLabelsPath = getLabelsFilterPath(propertySchema, currentPath);

    if (nestedLabelsPath) {
      return nestedLabelsPath;
    }
  }

  return null;
};

const clearLabelsFilterWhenUnannotated = (
  nextFormData,
  annotationPaths,
  schemaNode
) => {
  const unannotatedPath = annotationPaths?.unannotated;
  const labelsPath = getLabelsFilterPath(schemaNode);

  if (!unannotatedPath || !labelsPath) {
    return nextFormData;
  }

  const unannotatedSelected = Boolean(
    getValueAtPath(nextFormData, unannotatedPath)
  );

  if (!unannotatedSelected) {
    return nextFormData;
  }

  return setValueAtPath(nextFormData, labelsPath, false);
};

const disableLabelsFilterForUnannotated = (
  nextUiSchema,
  schemaNode,
  formData = {}
) => {
  const annotationPaths = collectAnnotationFilterPaths(schemaNode);
  const unannotatedPath = annotationPaths.unannotated;
  const labelsPath = getLabelsFilterPath(schemaNode);

  if (
    !unannotatedPath ||
    !labelsPath ||
    !getValueAtPath(formData, unannotatedPath)
  ) {
    return nextUiSchema;
  }

  return setUiSchemaAtPath(nextUiSchema, labelsPath, (uiNode = {}) => ({
    ...uiNode,
    "ui:disabled": true,
  }));
};

const enforceAnnotationFilterExclusivity = (
  nextFormData,
  previousFormData,
  changedFieldId,
  schemaNode
) => {
  const annotationPaths = collectAnnotationFilterPaths(schemaNode);
  const annotatedPath = annotationPaths.annotated;
  const unannotatedPath = annotationPaths.unannotated;

  if (!annotatedPath || !unannotatedPath) {
    return nextFormData;
  }

  let resolvedFormData = nextFormData;
  const annotatedSelected = Boolean(getValueAtPath(nextFormData, annotatedPath));
  const unannotatedSelected = Boolean(
    getValueAtPath(nextFormData, unannotatedPath)
  );

  if (annotatedSelected && unannotatedSelected) {
    const changedKind = getAnnotationFilterKind(changedFieldId);

    if (changedKind === "annotated") {
      resolvedFormData = setValueAtPath(nextFormData, unannotatedPath, false);
    } else if (changedKind === "unannotated") {
      resolvedFormData = setValueAtPath(nextFormData, annotatedPath, false);
    } else {
      const previousAnnotatedSelected = Boolean(
        getValueAtPath(previousFormData, annotatedPath)
      );
      const previousUnannotatedSelected = Boolean(
        getValueAtPath(previousFormData, unannotatedPath)
      );

      if (!previousAnnotatedSelected && annotatedSelected) {
        resolvedFormData = setValueAtPath(nextFormData, unannotatedPath, false);
      } else if (!previousUnannotatedSelected && unannotatedSelected) {
        resolvedFormData = setValueAtPath(nextFormData, annotatedPath, false);
      } else {
        resolvedFormData = setValueAtPath(nextFormData, unannotatedPath, false);
      }
    }
  }

  return clearLabelsFilterWhenUnannotated(
    resolvedFormData,
    annotationPaths,
    schemaNode
  );
};

const findApplyAugmentationsProperty = (properties = [], schema = {}) =>
  properties.find((property) =>
    isApplyAugmentationsChoiceField(
      property.name,
      schema?.properties?.[property.name]
    )
  );

const ModifyOperationsObjectFieldTemplate = ({
  title,
  properties,
  uiSchema = {},
  schema,
  formData = {},
}) => {
  if (uiSchema?.["ui:title"] === false) {
    return <>{properties.map((property) => property.content)}</>;
  }

  const visibleTitle = title || schema?.title;
  const normalizedTitle = normalizeFieldName(visibleTitle);
  const applyAugmentationsProperty = findApplyAugmentationsProperty(
    properties,
    schema
  );

  if (applyAugmentationsProperty) {
    const applyAugmentationsSchema =
      schema?.properties?.[applyAugmentationsProperty.name] || {};
    const selectedAugmentationOption =
      formData?.[applyAugmentationsProperty.name] ??
      getDefaultChoiceValue(applyAugmentationsSchema);
    const shouldShowDetails = shouldShowAugmentationFields(
      selectedAugmentationOption
    );
    const detailProperties = properties.filter(
      (property) => property !== applyAugmentationsProperty
    );

    return (
      <section className="task-collection-section modify-augmentations-section">
        <div className="modify-augmentations-card">
          <div className="modify-augmentations-card__selector">
            {applyAugmentationsProperty.content}
          </div>

          {shouldShowDetails && detailProperties.length > 0 && (
            <div className="modify-augmentations-card__fields">
              <div className=" modify-augmentations-card__grid gap-2 d-flex flex-column">
                {detailProperties.map((property) => (
                  <div
                    key={property.name}
                    className={`task-grid__item ${getModifyOperationFieldClassName(
                      property.name,
                      detailProperties.length,
                      schema?.properties?.[property.name]
                    )}`}
                  >
                    {property.content}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  const booleanProperties =
    schema?.type === "object"
      ? properties.filter((property) => {
        const propertySchema = schema?.properties?.[property.name] || {};
        return propertySchema?.type === "boolean";
      })
      : [];
  const operationToggleProperty =
    schema?.type === "object"
      ? properties.find((property) => {
        const propertySchema = schema?.properties?.[property.name] || {};
        const normalizedPropertyName = normalizeFieldName(property.name);
        const normalizedPropertyTitle = normalizeFieldName(propertySchema?.title);

        return (
          propertySchema?.type === "boolean" &&
          (
            normalizedPropertyName === normalizedTitle ||
            normalizedPropertyTitle === normalizedTitle
          )
        );
      }) || (visibleTitle !== "root" && booleanProperties.length === 1
        ? booleanProperties[0]
        : null)
      : null;
  const isOperationGroup = Boolean(operationToggleProperty);

  if (isOperationGroup) {
    const operationSchema = schema?.properties?.[operationToggleProperty.name] || {};
    const operationTitle = getReadableOperationLabel(
      operationSchema?.title || operationToggleProperty.name || visibleTitle
    );
    const operationValue = formData?.[operationToggleProperty.name];
    const isEnabled = Boolean(operationValue ?? operationSchema?.default);
    const operationFields = properties.filter(
      (property) => property !== operationToggleProperty
    );

    return (
      <section
        className={`modify-operation-panel ${isEnabled ? "is-enabled" : ""}`}
      >
        <div className="modify-operation-panel__header">
          <div className="text">{operationTitle}</div>
          <div className="modify-operation-panel__toggle">
            {operationToggleProperty.content}
          </div>
        </div>

        {isEnabled && operationFields.length > 0 && (
          <div className="task-grid modify-operation-panel__fields">
            {operationFields.map((property) => (
              <div
                key={property.name}
                className={`task-grid__item ${getModifyOperationFieldClassName(
                  property.name,
                  operationFields.length,
                  schema?.properties?.[property.name]
                )}`}
              >
                {property.content}
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="task-collection-section modify-operations-section">
      <div className="d-flex flex-column gap-3">
        {properties.map((property) => (
          <div
            key={property.name}
            className={`task-grid__item ${getModifyOperationFieldClassName(
              property.name,
              properties.length,
              schema?.properties?.[property.name]
            )}`}
          >
            {property.content}
          </div>
        ))}
      </div>
    </section>
  );
};

export default function ModifyOperations({ datasetId, close, reload }) {
  const alertCtx = useContext(alertContext);
  const setAlert =
    typeof alertCtx === "function" ? alertCtx : alertCtx?.setAlert;
  const [schema, setSchema] = useState();
  const [uischema, setUischema] = useState();
  const [submited, setSubmited] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [loadingAction, setLoadingAction] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const defaultSplitRatiosRef = useRef(null);
  const url = `forms/data/modify_operations/${datasetId}`;
  const { data, loading, error, setLoad } = useFetch(url, "get");
  const steps = data?.steppers || [];
  const isLastStep = Boolean(schema) && activeStep === steps.length - 1;
  const totalSteps = steps.length || 1;
  const currentStepLabel =
    steps?.[activeStep] || schema?.title || (isLastStep ? "Review" : "Configuration");
  const currentStepNumber = Math.min(activeStep + 1, totalSteps);
  const visibleHeaderSteps =
    steps.length > 0 ? steps.slice(0, currentStepNumber) : [currentStepLabel];

  useEffect(() => {
    if (!data) return;

    setActiveStep(0);
    setSubmitError("");
    setSubmited(hydrateFormDataDefaults(data?.json_schema, {}));
    defaultSplitRatiosRef.current = null;
    setUischema(sanitizeUiSchema(data?.ui_schema || {}));
    setSchema(data?.json_schema);
  }, [data]);

  const updatedSchema = useMemo(
    () => replaceUnderscoresInSchemaTitles(schema),
    [schema]
  );

  const isSplitStep = useMemo(
    () =>
      /split/i.test(String(currentStepLabel || "")) ||
      isSplitObjectSchema(updatedSchema),
    [currentStepLabel, updatedSchema]
  );

  useEffect(() => {
    if (!isSplitStep || !updatedSchema) return;

    setSubmited((current) => {
      const hydrated = hydrateSplitFormData(updatedSchema, current, {
        initializeType: true,
      });
      if (!defaultSplitRatiosRef.current) {
        const sourceDefaults = hydrateSplitFormData(updatedSchema, {}, {
          initializeType: true,
        });
        defaultSplitRatiosRef.current = getSplitRatiosFromFormData(
          updatedSchema,
          sourceDefaults
        );
      }

      return JSON.stringify(hydrated) === JSON.stringify(current)
        ? current
        : hydrated;
    });
  }, [isSplitStep, updatedSchema]);

  const enhancedUiSchema = useMemo(() => {
    const taskLikeUiSchema = enhanceTaskLikeUiSchema(
      updatedSchema,
      uischema || {}
    );
    const modifyOperationsUiSchema = enhanceModifyOperationsUiSchema(
      updatedSchema,
      taskLikeUiSchema
    );

    const resolvedUiSchema = disableLabelsFilterForUnannotated(
      modifyOperationsUiSchema,
      updatedSchema,
      submited
    );

    return isSplitStep
      ? applyExperimentLayoutUi(updatedSchema, resolvedUiSchema)
      : resolvedUiSchema;
  }, [isSplitStep, submited, updatedSchema, uischema]);

  const formSchema = useMemo(
    () => (updatedSchema ? { ...updatedSchema, title: "" } : updatedSchema),
    [updatedSchema]
  );

  const renderedFormData = useMemo(
    () =>
      isSplitStep && updatedSchema
        ? hydrateSplitFormData(updatedSchema, submited)
        : submited,
    [isSplitStep, submited, updatedSchema]
  );

  const widgets = useMemo(
    () => ({
      ...buildTaskLikeWidgets(CustomWidget),
      modifyOperationCards: ModifyOperationChoiceWidget,
      modifyOptionCards: ModifyOptionCardsWidget,
      modifyAugmentationChoice: ModifyAugmentationChoiceWidget,
      modifyOperationSwitch: ModifyOperationSwitchWidget,
    }),
    []
  );

  const splitFields = useMemo(
    () => ({
      experimentSplitField: ExperimentSplitField,
      experimentSplitTypeField: ExperimentSplitTypeField,
    }),
    []
  );

  const postStepData = async (payload, step) => {
    const token = getCokkie("jwtToken");
    const response = await fetch(
      `${window["ENV"].API_URL}/forms/data/modify_operations/${datasetId}?step=${step}`,
      {
        method: "post",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(result?.message || "Failed to submit modify operation.");
    }

    return result;
  };

  const applyServerResult = (result, fallbackPayload = {}) => {
    setSchema(result?.json_schema ?? result?.summary);
    setUischema(sanitizeUiSchema(result?.ui_schema || {}));

    if (result?.summary) {
      setSubmited(result.summary);
      setSchema({ configuration: result.summary });
    } else if (fallbackPayload && Object.keys(fallbackPayload).length > 0) {
      setSubmited(fallbackPayload);
    }

    setActiveStep((prev) =>
      result?.active_step !== undefined ? result.active_step : prev + 1
    );
  };

  const onSubmit = async ({ formData }, e) => {
    e.preventDefault();
    setLoad(true);
    setLoadingAction("next");
    setSubmitError("");

    try {
      let payload = enforceAnnotationFilterExclusivity(
        { ...submited, ...formData },
        submited,
        "",
        formSchema
      );
      if (isSplitStep) {
        payload = hydrateSplitFormData(updatedSchema, payload, {
          initializeType: true,
        });
        const splitValidationMessage = getSplitValidationMessage(
          updatedSchema,
          payload,
          { allowZeroDefaultSplits: true }
        );
        if (splitValidationMessage) {
          throw new Error(splitValidationMessage);
        }
      }
      setSubmited(payload);
      const result = await postStepData(payload, activeStep);
      applyServerResult(result, payload);
    } catch (e) {
      const message = e.message || "Failed to move to the next step.";
      setSubmitError(message);
      setAlert?.({ message, status: "error" });
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };

  const back = async (e) => {
    e.preventDefault();
    setLoad(true);
    setLoadingAction("back");
    setSubmitError("");

    try {
      const previousStep = activeStep - 2;
      const result = await postStepData({ ...submited }, previousStep);

      setSchema(result?.json_schema ?? result?.summary);
      setUischema(sanitizeUiSchema(result?.ui_schema || {}));

      if (result?.summary) {
        setSubmited(result.summary);
        setSchema({ configuration: result.summary });
      }

      setActiveStep(result?.active_step ?? 0);
    } catch (e) {
      const message = e.message || "Failed to load the previous step.";
      setSubmitError(message);
      setAlert?.({ message, status: "error" });
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };

  const final = async (e) => {
    e.preventDefault();
    setLoad(true);
    setLoadingAction("submit");
    setSubmitError("");

    try {
      const payload = { ...(schema?.configuration || submited) };
      const result = await postStepData(payload, activeStep);
      setAlert?.(result?.detail || "Modify operation submitted successfully.");
      close?.("");
      reload?.();
    } catch (e) {
      console.log("event", e);
      const message = e.message || "Failed to submit modify operation.";
      setSubmitError(message);
      setAlert?.({ message, status: "error" });
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };

  const getStepDescription = (stepLabel = "") => {
    const normalized = String(stepLabel || "").toLowerCase();

    if (isLastStep || normalized.includes("summary") || normalized.includes("review")) {
      return "Review the selected dataset modification settings before applying the operation.";
    }

    if (activeStep === 0) {
      return "Choose the operation and configure the required dataset settings for this step.";
    }

    if (normalized.includes("label")) {
      return "Adjust labels and metadata needed for this dataset modification.";
    }

    if (normalized.includes("content") || normalized.includes("data")) {
      return "Update the content handling and transformation details for this operation.";
    }

    return "Complete the required information for this step before continuing.";
  };

  return (
    <main
      className={`open-form evaluate-model-ui h-100 d-flex flex-column dataset-flow-shell task-collection-shell modify-operations-shell ${isSplitStep ? "experiment-flow-shell" : ""}`}
    >
      <header className="d-flex align-items-start justify-content-between p-3 dataset-flow-shell__header task-collection-shell__header">
        <div className="d-flex gap-2 align-items-start w-100">
          <ModifyOperationsIcon className="wf-icon-blue bg-primary text-white" />
          <div className="em-header flex-fill">
            <div className="d-flex justify-content-between w-100">
              <h2 className="main-title mb-0">Create New Dataset Version</h2>
              <button className="plain-button2 p-0" onClick={() => close?.("")}>
                <CloseIcon className="wf-icon text-secondary" />
              </button>
            </div>
            <p className="sub-title mb-0">
              Step {currentStepNumber} of {totalSteps}: {currentStepLabel}
            </p>
          </div>
        </div>
      </header>

      <section className="dataset-wizard__card flex-fill w-100 dataset-flow-shell__body task-collection-shell__body">
        {loading ? (
          <div className="dataset-flow-shell__loader task-collection-shell__loader">
            <Loader />
          </div>
        ) : error ? (
          <ErrorScreen message={error?.message || "Unable to load modify operations"} />
        ) : schema ? (
          <>
            <div className="form-shell__intro">
              <div className="task-collection-shell__eyebrow">
                <div className="task-collection-shell__step-flow">
                  {visibleHeaderSteps.map((stepLabel, index) => {
                    const stepNumber = index + 1;
                    const isCurrentHeaderStep = stepNumber === currentStepNumber;

                    return (
                      <div
                        key={`${stepLabel}-${stepNumber}`}
                        className="task-collection-shell__step-item"
                      >
                        {isCurrentHeaderStep ? (
                          <span className="task-collection-shell__step-badge">
                            Step {stepNumber}
                          </span>
                        ) : (
                          <span className="task-collection-shell__step-marker">
                            {stepNumber}
                          </span>
                        )}
                        <span
                          className={`task-collection-shell__step-name ${isCurrentHeaderStep
                            ? "task-collection-shell__step-name--active"
                            : ""
                            }`}
                          style={{ color: '#b1c7e8' }}
                        >
                          {stepLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="mb-0 from-shell-sub-text">{getStepDescription(currentStepLabel)}</p>
            </div>

            {submitError && (
              <Alert color="danger" className="mb-3 py-2">
                {submitError}
              </Alert>
            )}

            {!isLastStep ? (
              <div className="task-collection-stage">
                <Form
                  id="modify-operations-form"
                  className="task-collection-form"
                  schema={formSchema}
                  uiSchema={enhancedUiSchema}
                  widgets={widgets}
                  fields={isSplitStep ? splitFields : undefined}
                  formData={renderedFormData}
                  onChange={({ formData: changedFormData }, changedFieldId) => {
                    setSubmited((prev) => {
                      const nextFormData =
                        applyDependencyDefaultsOnSelectionChange(
                          formSchema,
                          { ...prev, ...changedFormData },
                          prev
                        );

                      return enforceAnnotationFilterExclusivity(
                        nextFormData,
                        prev,
                        changedFieldId,
                        formSchema
                      );
                    });
                  }}
                  onSubmit={onSubmit}
                  noValidate={isSplitStep}
                  noHtml5Validate={isSplitStep}
                  showErrorList={false}
                  FieldTemplate={
                    isSplitStep
                      ? ExperimentFieldTemplate
                      : ModifyOperationsFieldTemplate
                  }
                  ObjectFieldTemplate={
                    isSplitStep
                      ? ExperimentObjectFieldTemplate
                      : ModifyOperationsObjectFieldTemplate
                  }
                  formContext={
                    isSplitStep
                      ? {
                        schema: updatedSchema,
                        formData: renderedFormData,
                        currentStepLabel,
                        allowZeroDefaultSplits: true,
                        applySplitPreset: (preset, { asCustom = true } = {}) =>
                          setSubmited((current) => {
                            let next = applySplitPresetToFormData(
                              updatedSchema,
                              current,
                              preset
                            );
                            if (asCustom) {
                              const customValue =
                                getCustomSplitTypeValueFromSchema(updatedSchema);
                              if (customValue !== undefined) {
                                next = applySplitTypeToFormData(
                                  updatedSchema,
                                  next,
                                  customValue
                                );
                              }
                            }
                            return next;
                          }),
                        applySplitType: (
                          value,
                          { restoreDefault = false, applyCustomPreset = false } = {}
                        ) =>
                          setSubmited((current) => {
                            let next = applySplitTypeToFormData(
                              updatedSchema,
                              current,
                              value
                            );
                            if (restoreDefault && defaultSplitRatiosRef.current) {
                              next = applySplitPresetToFormData(
                                updatedSchema,
                                next,
                                defaultSplitRatiosRef.current
                              );
                            } else if (applyCustomPreset) {
                              next = applySplitPresetToFormData(
                                updatedSchema,
                                next,
                                CUSTOM_SPLIT_PRESET
                              );
                            }
                            return next;
                          }),
                      }
                      : undefined
                  }
                >
                  <Button className="d-none" />
                </Form>
              </div>
            ) : (
              <div className="px-2 task-collection-summary">
                <DataConfiguration2 expData={schema} />
              </div>
            )}
          </>
        ) : (
          <ErrorScreen
            message={data?.detail ?? "No form Found"}
            sub={data?.detail}
          />
        )}
      </section>

      {schema && (
        <div className="dataset-footer">
          <div className="dataset-footer__actions">
            {activeStep === 0 ? (
              <span></span>
            ) : (
              <Button
                type="button"
                className="bg-secondary mt-0 text-white py-2"
                onClick={back}
                disabled={loading}
              >
                {loadingAction === "back" ? (
                  <FontAwesomeIcon className="me-2" icon={faSpinner} spin />
                ) : (
                  <BackIcon className="wf-icon me-2 task-collection-shell__back-icon" />
                )}
                Back
              </Button>
            )}

            {!isLastStep ? (
              <Button
                type="submit"
                form="modify-operations-form"
                color="primary"
                className="py-2 m-0 fw-bold"
                disabled={loading}
              >
                Next Step&nbsp;
                {loadingAction === "next" ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <ArrowRightIcon className="wf-icon text-white" />
                )}
              </Button>
            ) : (
              <Button
                type="button"
                color="primary"
                className="py-2 m-0 fw-bold"
                onClick={final}
                disabled={loading}
              >
                Apply Operation&nbsp;
                {loadingAction === "submit" ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <CheckIcon className="wf-icon text-white" />
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
